import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { DEFAULT_BRANDING, type LeadConversions } from "@/types/ui/branding";
import type { LeadConversionsSettingsProps } from "@/types/chat-widget";

const GOOGLE_SEND_TO_RE = /^AW-\d+\/[\w-]+$/;

interface LeadConversionsDraft {
  enabled: boolean;
  googleSendTo: string;
  metaLead: boolean;
}

const toDraft = (saved: LeadConversions | undefined): LeadConversionsDraft => ({
  enabled: saved?.enabled === true,
  googleSendTo: saved?.googleSendTo ?? "",
  metaLead: saved?.metaLead !== false,
});

/**
 * "Count chat leads as ad conversions": a lead left in the chat fires the
 * customer's own Google Ads and Meta tags, the way their contact form did
 * (src/lib/leadConversions.ts → widget.js / widget-v2.js). Switching it on
 * also gives the Genie its requestContactDetails tool (handle-agent's
 * syncContactDetailsTool). Edited as one form and saved in a single genie
 * update, so the tool and the conversion settings always land together; a
 * change reaches the live widget without new embed code.
 */
export const LeadConversionsSettings = ({ genie }: LeadConversionsSettingsProps) => {
  const fieldId = useId();
  const update = useUpdateAgent();
  const saved = genie.branding?.embedStyle?.leadConversions;
  const savedDraft = toDraft(saved);
  const savedKey = JSON.stringify(savedDraft);

  const [draft, setDraft] = useState<LeadConversionsDraft>(savedDraft);
  useEffect(() => setDraft(JSON.parse(savedKey) as LeadConversionsDraft), [savedKey]);

  const sendTo = draft.googleSendTo.trim();
  const sendToValid = sendTo === "" || GOOGLE_SEND_TO_RE.test(sendTo);
  const dirty = JSON.stringify({ ...draft, googleSendTo: sendTo }) !== savedKey;
  const change = (patch: Partial<LeadConversionsDraft>) => setDraft((prev) => ({ ...prev, ...patch }));

  const save = () =>
    update.mutate({
      agent: genie,
      updateData: {
        branding: {
          ...DEFAULT_BRANDING,
          ...genie.branding,
          embedStyle: {
            ...genie.branding?.embedStyle,
            leadConversions: { enabled: draft.enabled, googleSendTo: sendTo || null, metaLead: draft.metaLead },
          },
        },
      },
    });

  return (
    <div className="space-y-4 rounded-xl border border-genie-border bg-white p-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <label htmlFor={`${fieldId}-enabled`} className="text-sm font-semibold text-gray-900">
            Count chat leads as ad conversions
          </label>
          <p className="mt-1 text-sm text-gray-500">
            When a visitor leaves their details in the chat, your Google Ads and Meta tags count it as a lead, like your
            contact form does. Your Genie also offers a contact form when a visitor shows interest in buying.
          </p>
        </div>
        <div className="flex min-h-11 min-w-11 shrink-0 items-center justify-center">
          <Switch
            id={`${fieldId}-enabled`}
            checked={draft.enabled}
            disabled={update.isPending}
            onCheckedChange={(checked) => change({ enabled: checked })}
          />
        </div>
      </div>

      {draft.enabled && (
        <div className="space-y-4 border-t border-genie-border pt-4">
          <div>
            <label htmlFor={`${fieldId}-google`} className="mb-2 block text-sm font-semibold text-gray-700">
              Google Ads conversion <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <input
              id={`${fieldId}-google`}
              type="text"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              value={draft.googleSendTo}
              disabled={update.isPending}
              onChange={(e) => change({ googleSendTo: e.target.value })}
              placeholder="AW-123456789/AbCdEfGhIj"
              aria-invalid={!sendToValid}
              aria-describedby={`${fieldId}-google-help`}
              className="w-full min-w-0 rounded-xl border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-genie-primary sm:text-sm"
            />
            <p id={`${fieldId}-google-help`} className={sendToValid ? "mt-2 text-xs text-gray-500" : "mt-2 text-xs text-genie-error"}>
              {sendToValid
                ? "The conversion's send_to value, from Google Ads → Goals → Conversions → Tag setup."
                : "This should look like AW-123456789/AbCdEfGhIj."}
            </p>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <label htmlFor={`${fieldId}-meta`} className="text-sm font-semibold text-gray-700">
                Fire Meta Lead event
              </label>
              <p className="mt-1 text-sm text-gray-500">Uses the Meta Pixel already on your website.</p>
            </div>
            <div className="flex min-h-11 min-w-11 shrink-0 items-center justify-center">
              <Switch
                id={`${fieldId}-meta`}
                checked={draft.metaLead}
                disabled={update.isPending}
                onCheckedChange={(checked) => change({ metaLead: checked })}
              />
            </div>
          </div>

          <p className="text-xs leading-relaxed text-gray-500">
            Using Google Tag Manager? Trigger on the <code className="font-mono">helpgenie_lead</code> event instead.
            Your Google and Meta tags must already be on the page.
          </p>
        </div>
      )}

      {(dirty || update.isError || update.isSuccess) && (
        <div className="flex flex-wrap items-center gap-3 border-t border-genie-border pt-4">
          {dirty && (
            <>
              <Button
                type="button"
                onClick={save}
                disabled={!sendToValid || update.isPending}
                className="min-h-11 bg-gradient-to-r from-genie-primary to-genie-secondary px-6 font-semibold text-white shadow-lg shadow-genie-primary/30 transition-all duration-200 hover:scale-105 hover:from-genie-primary-hover hover:to-genie-secondary hover:shadow-xl hover:shadow-genie-primary/40 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
              >
                {update.isPending ? "Saving…" : "Save"}
              </Button>
              <Button type="button" variant="outline" className="min-h-11" disabled={update.isPending} onClick={() => setDraft(savedDraft)}>
                Cancel
              </Button>
            </>
          )}
          {update.isError ? (
            <p role="alert" className="text-sm text-genie-error">
              Couldn’t save. Please try again.
            </p>
          ) : (
            !dirty && update.isSuccess && (
              <p role="status" className="text-sm text-gray-500">
                Saved. Live on your website straight away.
              </p>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default LeadConversionsSettings;
